import { FC } from "react";
import { useWatch } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  LoadingButton,
} from "@altimateai/lego";
import { IconCalendar, IconList, IconCheck, IconArrowLeft } from "@tabler/icons-react";
import SchedulesList from "./SchedulesList";
import ScheduleSlackField from "./ScheduleSlackField";
import ScheduleEmailsField from "./ScheduleEmailsField";
import ScheduleDayPicker from "./ScheduleDayPicker";
import { formatSuccessFrequency } from "./scheduleDisplay";
import {
  ScheduleDialogProps,
  FREQUENCY_OPTIONS,
  HOUR_OPTIONS,
  CONTENT_SCOPE_OPTIONS,
  useScheduleDialog,
} from "./useScheduleForm";

const ScheduleDialog: FC<ScheduleDialogProps> = props => {
  const { sessionId, sessionName, open, onOpenChange, schedule, initialView } = props;
  const ctx = useScheduleDialog(sessionId, sessionName, open, onOpenChange, schedule, initialView);
  const { form, view, setView, isEditing, isPending, formValues, onSubmit } = ctx;
  // Targeted subscriptions — only this component re-renders when these
  // fields change (vs form.watch which re-renders on every field).
  // `watchedFrequency` drives the Day-of-Week / Day-of-Month picker visibility.
  const watchedSlackWebhookId = useWatch({ control: form.control, name: "slackWebhookId" });
  const watchedFrequency = useWatch({ control: form.control, name: "frequency" });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:al-max-w-[600px]">
        {view === "form" && (
          <>
            <DialogHeader>
              <DialogTitle>
                <Stack direction="row" align="center" gap="sm">
                  <IconCalendar className="al-h-5 al-w-5" />
                  {isEditing ? "Edit Schedule" : "Schedule Report"}
                </Stack>
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="al-space-y-4 al-py-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Schedule Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter schedule name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contentScope"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Content to Send</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="al-w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CONTENT_SCOPE_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                              <div>
                                <div>{opt.label}</div>
                                <div className="al-text-xs al-text-muted-foreground">
                                  {opt.description}
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="al-space-y-4">
                  <div className="al-grid al-gap-4 al-grid-cols-1 md:al-grid-cols-2">
                    <FormField
                      control={form.control}
                      name="frequency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Frequency</FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger className="al-w-full">
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {FREQUENCY_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="hour"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Time (UTC)</FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger className="al-w-full">
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {HOUR_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <ScheduleDayPicker control={form.control} frequency={watchedFrequency} />
                </div>
                <ScheduleSlackField control={form.control} open={open} />
                <ScheduleEmailsField
                  control={form.control}
                  emailsOptional={!!watchedSlackWebhookId}
                />
                <DialogFooter>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setView("list")}
                    className="al-mr-auto"
                  >
                    <IconList className="al-h-4 al-w-4 al-mr-2" />
                    View Schedules
                  </Button>
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <LoadingButton type="submit" loading={isPending}>
                    {isEditing ? "Save Changes" : "Create Schedule"}
                  </LoadingButton>
                </DialogFooter>
              </form>
            </Form>
          </>
        )}
        {view === "success" && (
          <>
            <DialogHeader>
              <DialogTitle>
                <Stack direction="row" align="center" gap="sm">
                  <IconCheck className="al-h-5 al-w-5 al-text-green-600" />
                  Schedule Created
                </Stack>
              </DialogTitle>
            </DialogHeader>
            <div className="al-py-6 al-text-center al-space-y-2">
              <p className="al-text-sm al-text-muted-foreground">
                <strong>{formValues.name}</strong> has been scheduled. Reports will be sent{" "}
                {formatSuccessFrequency(
                  formValues.frequency,
                  formValues.hour,
                  formValues.dayOfWeek,
                  formValues.dayOfMonth
                )}
                .
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button onClick={() => setView("list")}>
                <IconList className="al-h-4 al-w-4 al-mr-2" />
                View Schedules
              </Button>
            </DialogFooter>
          </>
        )}
        {view === "list" && (
          <>
            <DialogHeader>
              <DialogTitle>
                <Stack direction="row" align="center" gap="sm">
                  {sessionId && (
                    <Button variant="ghost" size="icon" title="Back" onClick={ctx.resetToNewForm}>
                      <IconArrowLeft className="al-h-4 al-w-4" />
                    </Button>
                  )}
                  Schedules
                </Stack>
              </DialogTitle>
            </DialogHeader>
            <div className="al-py-2 al-max-h-[60vh] al-overflow-y-auto">
              <SchedulesList
                sessionId={sessionId}
                onEdit={s => {
                  ctx.setEditingSchedule(s);
                  setView("form");
                }}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleDialog;
